import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useLocation, useNavigate } from 'react-router-dom';

import { requirementForPath } from 'constants/navPermissions';
import { firstVisiblePath } from 'helpers/navVisibility';
import sidebarNav from 'partner/config/navigation';
import useAuthStore from 'partner/stores/useAuthStore';
import { describeModuleKey } from 'constants/moduleLabels';
import { palette, typography, radius, borderWidth, size } from 'theme/tokens';

/**
 * 403 for the partner admin portal — the twin of
 * admin/pages/AccessDeniedPage.jsx, and now laid out like it too.
 *
 * Two files rather than one shared component because partner code has never
 * imported from `src/admin`; the COPY is deliberately identical, so an operator
 * who works in both portals reads the same sentence and does the same thing.
 * The colours resolve to the partner's brand rather than the CRM's purple,
 * which is the one difference on purpose.
 *
 * Why it names the permission, and why it says "sign in again": see the admin
 * twin's header — the reasoning is the same, and it is written out there.
 */
export default function AccessDeniedPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const perms = useAuthStore((s) => s.perms);

  // See the admin twin: the dashboard is gated too, so "back" has to mean
  // "somewhere this role can open", and nowhere means no button.
  const home = firstVisiblePath(sidebarNav, perms);

  const requirement = requirementForPath(pathname);
  const options = Array.isArray(requirement) ? requirement : requirement ? [requirement] : [];
  // Named the way the role editor names them, so the operator asks for a thing
  // the administrator can actually find. See constants/moduleLabels.js.
  const codes = options
    .filter((option) => option && option.side)
    .map(({ side, module, subModule }) =>
      describeModuleKey(subModule ? `${side}:${module}/${subModule}` : `${side}:${module}`, 'partner'),
    );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        textAlign: 'center',
        px: 3,
      }}
    >
      <LockOutlinedIcon sx={{ fontSize: typography.fontSize[64], color: 'rgba(var(--pa-brand-primary-rgb, 124, 92, 252), 0.35)', mb: 2 }} />
      <Typography variant="h4" sx={{ mb: 1, fontWeight: typography.fontWeight.bold }}>
        You do not have access to this page
      </Typography>
      <Typography sx={{ color: palette.neutral[470], mb: codes.length ? 2 : 3, maxWidth: size[560] }}>
        Your role does not include this screen. Ask an administrator to grant it, then sign out and back in — a new
        permission does not reach a session that is already open.
      </Typography>

      {codes.length > 0 && (
        <Typography
          component="div"
          sx={{
            color: palette.neutral[580],
            mb: 3,
            fontSize: typography.fontSize[13],
            fontFamily: typography.fontFamily.monoSystem,
            /* The tint follows the partner's brand rather than the CRM's
               purple; the literal fallback is mandatory, as at every var() call
               site in this portal. */
            backgroundColor: 'rgba(var(--pa-brand-primary-rgb, 124, 92, 252), 0.06)',
            border: `${borderWidth.thin} solid rgba(var(--pa-brand-primary-rgb, 124, 92, 252), 0.18)`,
            borderRadius: `${radius.md}px`,
            px: 2,
            py: 1.25,
          }}
        >
          {/* Plural only when it genuinely is: a two-tab screen opens for either. */}
          {codes.length > 1 ? 'Requires any of: ' : 'Requires: '}
          {codes.join('  ·  ')}
        </Typography>
      )}

      <Box sx={{ display: 'flex', gap: 1.5 }}>
        {home && (
          <Button variant="contained" color="primary" onClick={() => navigate(home)}>
            {home === '/partnerAdmin' ? 'Back to Dashboard' : 'Go to a page you can open'}
          </Button>
        )}
        {/* -1 rather than a fixed route: the operator arrived from somewhere, and
            that somewhere is a screen they could actually open. */}
        <Button variant="outlined" onClick={() => navigate(-1)}>
          Go Back
        </Button>
      </Box>
    </Box>
  );
}
